import { useCallback, useId, useMemo, useReducer, useRef, useState } from 'react';
import type {
  WizardBlockV2,
  WizardStepV2,
  WizardEvent,
  WizardEventContext,
  HandlerResult,
  ResolvedWizardButtons,
  StepChannel,
  StepSnapshot,
} from './types';
import { wizardReducer, createInitialWizardState } from './wizardReducer';
import { useWizardChannels } from './hooks/useWizardChannels';
import { createWizardPipeline } from './wizardPipeline';
import { deriveButtonState } from './buttonDeriver';
import { INITIAL_SNAPSHOT } from './snapshotReducer';

export interface UseWizardOrchestratorV2Options {
  block: WizardBlockV2;
  onSuccess?: (data: unknown) => void;
  onCancel?: () => void;
}

export interface WizardOrchestratorV2Result {
  wizardId: string;
  currentStepIndex: number;
  currentStep: WizardStepV2 | undefined;
  visibleSteps: WizardStepV2[];
  activeChannel: StepChannel | null;
  meta: Record<string, unknown>;
  stepData: Record<string, Record<string, unknown>>;
  buttons: ResolvedWizardButtons;
  isBusy: boolean;
  error: string | null;
  handleNext(): void;
  handleBack(): void;
  handleCancel(): void;
  handleSecondaryClick(buttonId: string): void;
  goToStep(stepId: string): void;
}

export function useWizardOrchestratorV2({
  block,
  onSuccess,
  onCancel,
}: UseWizardOrchestratorV2Options): WizardOrchestratorV2Result {
  const reactId = useId();
  const wizardId = block.$id ?? `wizard-${reactId}`;

  const [state, dispatch] = useReducer(
    wizardReducer,
    block.context?.initialMeta,
    createInitialWizardState,
  );

  const stateRef = useRef(state);
  stateRef.current = state;

  // Counter to force re-render when any signal is received (dirty, ready, etc.)
  const [, forceRender] = useState(0);

  const { activateStep, getChannel, disposeAll } = useWizardChannels({
    wizardId,
    onSignalReceived: () => {
      const ch = activeChannelRef.current;
      if (ch && stateRef.current.phase === 'step_active') {
        const snap = ch.getSnapshot();
        if (snap.ready) {
          dispatch({ type: 'CHANNEL_READY' });
        }
      }
      forceRender((c) => c + 1);
    },
  });

  const activeChannelRef = useRef<StepChannel | null>(null);

  const visibleSteps = useMemo(
    () => block.steps.filter((s) => !s.visible || s.visible === 'true'),
    [block.steps],
  );

  const currentStep = visibleSteps[state.currentStepIndex];

  const ensureChannel = useCallback(
    (stepId: string): StepChannel => {
      const ch = activateStep(stepId);
      activeChannelRef.current = ch;
      return ch;
    },
    [activateStep],
  );

  // Activate the first step on mount
  const hasInitialized = useRef(false);
  if (!hasInitialized.current && visibleSteps.length > 0) {
    hasInitialized.current = true;
    dispatch({ type: 'SCHEMA_LOADED' });
    dispatch({ type: 'ACTIVATE_STEP', index: 0 });
    ensureChannel(visibleSteps[0].id);
  }

  const dispatchToHandler = useCallback(
    async (event: WizardEvent, buttonId?: string): Promise<HandlerResult | undefined> => {
      const step = visibleSteps[stateRef.current.currentStepIndex];
      if (!step) return undefined;

      const channel = getChannel(step.id) ?? activeChannelRef.current;
      if (!channel) return undefined;

      const snapshot: StepSnapshot = channel.getSnapshot();
      const pipeline = createWizardPipeline(channel);

      const wizardContext: WizardEventContext = {
        event,
        stepId: step.id,
        buttonId,
        meta: stateRef.current.meta,
        stepData: stateRef.current.stepData,
        snapshot,
        channel,
        mode: (stateRef.current.meta.mode as string) ?? 'create',
        updateMeta: (updates) => dispatch({ type: 'UPDATE_META', updates }),
        setStepData: (sid, data) => dispatch({ type: 'SET_STEP_DATA', stepId: sid, data }),
        pipeline,
      };

      dispatch({ type: 'HANDLER_STARTED' });

      try {
        const { ActionRegistry } = await import('@packages/table-builder');
        await ActionRegistry.ensureActions([block.handler]);
        const action = ActionRegistry.resolveAction(block.handler);
        if (!action) {
          console.warn(`[WizardOrchestratorV2] No handler action found for "${block.handler}"`);
          dispatch({ type: 'HANDLER_COMPLETED' });
          return undefined;
        }
        const ctx = {
          pk: '',
          row: {},
          refreshTable: () => {},
          params: {},
          wizardContext: wizardContext as unknown as Record<string, unknown>,
        };
        const result = await action.execute(ctx as any);
        dispatch({ type: 'HANDLER_COMPLETED' });
        return result as HandlerResult | undefined;
      } catch (err) {
        dispatch({
          type: 'SET_ERROR',
          error: {
            phase: 'handler_running',
            message: err instanceof Error ? err.message : String(err),
            recoverable: true,
            stepId: step.id,
          },
        });
        return { blocked: true };
      }
    },
    [block.handler, visibleSteps, getChannel],
  );

  const processResult = useCallback(
    (result: HandlerResult | undefined) => {
      if (!result || result.blocked) return;

      if (result.goBack) {
        const prevIndex = Math.max(stateRef.current.currentStepIndex - 1, 0);
        dispatch({ type: 'ACTIVATE_STEP', index: prevIndex });
        ensureChannel(visibleSteps[prevIndex].id);
        return;
      }

      if (result.complete) {
        dispatch({ type: 'START_COMPLETING' });
        onSuccess?.(stateRef.current.stepData);
        dispatch({ type: 'COMPLETED' });
        return;
      }

      if (result.goToStep) {
        const targetIndex = visibleSteps.findIndex((s) => s.id === result.goToStep);
        if (targetIndex >= 0) {
          dispatch({ type: 'ACTIVATE_STEP', index: targetIndex });
          ensureChannel(visibleSteps[targetIndex].id);
        }
        return;
      }

      if (result.advance) {
        const nextIndex = stateRef.current.currentStepIndex + 1;
        if (nextIndex < visibleSteps.length) {
          dispatch({ type: 'ACTIVATE_STEP', index: nextIndex });
          ensureChannel(visibleSteps[nextIndex].id);
        } else {
          dispatch({ type: 'START_COMPLETING' });
          onSuccess?.(stateRef.current.stepData);
          dispatch({ type: 'COMPLETED' });
        }
      }
    },
    [visibleSteps, ensureChannel, onSuccess],
  );

  const handleNext = useCallback(() => {
    void dispatchToHandler('NEXT').then(processResult);
  }, [dispatchToHandler, processResult]);

  const handleBack = useCallback(() => {
    void dispatchToHandler('BACK').then(processResult);
  }, [dispatchToHandler, processResult]);

  const handleCancel = useCallback(() => {
    void dispatchToHandler('CANCEL').then((result) => {
      if (!result || result.blocked) return;
      dispatch({ type: 'START_CANCELLING' });
      onCancel?.();
      dispatch({ type: 'CANCELLED' });
      disposeAll();
    });
  }, [dispatchToHandler, onCancel, disposeAll]);

  const handleSecondaryClick = useCallback(
    (buttonId: string) => {
      void dispatchToHandler('SECONDARY_CLICK', buttonId).then(processResult);
    },
    [dispatchToHandler, processResult],
  );

  const goToStep = useCallback(
    (stepId: string) => {
      const targetIndex = visibleSteps.findIndex((s) => s.id === stepId);
      if (targetIndex >= 0 && targetIndex <= state.currentStepIndex) {
        dispatch({ type: 'ACTIVATE_STEP', index: targetIndex });
        ensureChannel(visibleSteps[targetIndex].id);
      }
    },
    [visibleSteps, state.currentStepIndex, ensureChannel],
  );

  const snapshot = activeChannelRef.current?.getSnapshot() ?? INITIAL_SNAPSHOT;

  const buttons = deriveButtonState({
    wizardButtons: block.buttons,
    stepButtons: currentStep?.buttons,
    stepIndex: state.currentStepIndex,
    totalSteps: visibleSteps.length,
    phase: state.phase,
    snapshot,
    hasCancelHandler: onCancel != null,
    mode: (state.meta.mode as string) ?? 'create',
    meta: state.meta,
  });

  return {
    wizardId,
    currentStepIndex: state.currentStepIndex,
    currentStep,
    visibleSteps,
    activeChannel: activeChannelRef.current,
    meta: state.meta,
    stepData: state.stepData,
    buttons,
    isBusy: state.phase === 'handler_running' || state.phase === 'transitioning',
    error: state.error?.message ?? null,
    handleNext,
    handleBack,
    handleCancel,
    handleSecondaryClick,
    goToStep,
  };
}
